import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, RefreshCcw, Mail, MessageSquare, Bell, AlertTriangle,
  CheckCircle2, Ban, ShieldCheck, ShieldX,
} from 'lucide-react';
import { toast } from 'sonner';

const WINDOWS = [
  { key: '24h', label: '24 hours' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
];

const CHANNELS = [
  { key: 'in_app', label: 'In-app', icon: Bell },
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'sms', label: 'SMS', icon: MessageSquare },
];

const fmt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());
const when = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const ProviderPill = ({ ok, label }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${
      ok ? 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-300'
         : 'bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-900/40 dark:text-rose-300'
    }`}
    data-testid={`provider-pill-${label.toLowerCase().replace(/\W+/g, '-')}`}
  >
    {ok ? <ShieldCheck className="w-3 h-3" /> : <ShieldX className="w-3 h-3" />} {label}
  </span>
);

const TotalCard = ({ channel, label, icon: Icon, data }) => {
  const failed = data?.failed || 0;
  return (
    <Card data-testid={`total-card-${channel}`} className="overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Icon className="w-4 h-4 text-cyan-600" /> {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        <div className="flex justify-between"><span className="text-muted-foreground">Attempted</span><span className="font-semibold" data-testid={`total-${channel}-attempted`}>{fmt(data?.attempted)}</span></div>
        {channel !== 'in_app' && (
          <div className="flex justify-between"><span className="text-muted-foreground">Sent</span><span className="font-semibold">{fmt(data?.sent)}</span></div>
        )}
        <div className="flex justify-between">
          <span className="text-muted-foreground">Delivered</span>
          <span className="font-semibold text-emerald-600" data-testid={`total-${channel}-delivered`}>
            {channel === 'in_app' ? fmt(data?.delivered)
              : channel === 'sms' ? fmt(data?.provider_confirmed_delivered)
              : '—'}
          </span>
        </div>
        {channel !== 'in_app' && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Failed</span>
            <span className={`font-semibold ${failed ? 'text-rose-600' : ''}`} data-testid={`total-${channel}-failed`}>{fmt(failed)}</span>
          </div>
        )}
        {channel !== 'in_app' && (
          <div className="flex justify-between"><span className="text-muted-foreground">Suppressed</span><span className="font-medium text-amber-600">{fmt(data?.suppressed)}</span></div>
        )}
      </CardContent>
    </Card>
  );
};

const Cell = ({ ch, channel }) => {
  if (!ch || (!ch.attempted && !(ch.delivered))) {
    return <td className="px-3 py-2 text-center text-muted-foreground/50">—</td>;
  }
  const failed = ch.failed || 0;
  return (
    <td className="px-3 py-2 align-top" data-testid={`cell-${channel}`}>
      <div className="flex flex-col gap-0.5 text-xs">
        <span>att <b>{fmt(ch.attempted)}</b>{channel !== 'in_app' && <> · sent <b>{fmt(ch.sent)}</b></>}</span>
        <span>
          {channel === 'in_app'
            ? <span className="text-emerald-600">delivered <b>{fmt(ch.delivered)}</b></span>
            : channel === 'email'
              ? <span className="text-muted-foreground italic">delivery n/a</span>
              : <span className="text-emerald-600">delivered <b>{fmt(ch.delivered || 0)}</b></span>}
        </span>
        {channel !== 'in_app' && (
          <span className={failed ? 'text-rose-600 font-semibold' : 'text-muted-foreground'}>
            failed <b>{fmt(failed)}</b>{ch.suppressed ? ` · supp ${fmt(ch.suppressed)}` : ''}{ch.retry ? ` · retry ${fmt(ch.retry)}` : ''}
          </span>
        )}
        {ch.last_error && (
          <span className="text-rose-500 truncate max-w-[220px]" title={ch.last_error}>⚠ {ch.last_error}</span>
        )}
        <span className="text-muted-foreground/70">last {when(ch.last_attempt).replace(',', '')}</span>
        {ch.provider_id && <span className="text-muted-foreground/60 truncate max-w-[200px]" title={ch.provider_id}>id {ch.provider_id}</span>}
      </div>
    </td>
  );
};

const DeliveryHealth = () => {
  const navigate = useNavigate();
  const [win, setWin] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (w) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/delivery-health?window=${w}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load delivery health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(win); }, [win, load]);

  const providers = data?.providers || {};
  const totals = data?.totals || {};
  const failures = data?.recent_failures || [];

  return (
    <div className="min-h-screen bg-background text-foreground p-4 md:p-8" data-testid="delivery-health-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="delivery-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Delivery Health</h1>
              <p className="text-sm text-muted-foreground">Operational email, SMS &amp; in-app delivery — reported per channel.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border overflow-hidden" data-testid="window-selector">
              {WINDOWS.map((w) => (
                <button
                  key={w.key}
                  onClick={() => setWin(w.key)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${win === w.key ? 'bg-cyan-600 text-white' : 'bg-transparent hover:bg-muted'}`}
                  data-testid={`window-${w.key}`}
                >{w.label}</button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={() => load(win)} data-testid="delivery-refresh-btn">
              <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Provider config */}
        <div className="flex flex-wrap items-center gap-2">
          <ProviderPill ok={providers.sendgrid_configured} label="SendGrid" />
          <ProviderPill ok={providers.twilio_configured} label="Twilio" />
          <Badge variant="outline" data-testid="env-badge">env: {providers.app_env || '—'}</Badge>
          {providers.email_delivery_paused && <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Email paused</Badge>}
          {providers.sms_delivery_paused && <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">SMS paused</Badge>}
        </div>

        {/* Totals */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <TotalCard channel="in_app" label="In-app" icon={Bell} data={totals.in_app} />
          <TotalCard channel="email" label="Email" icon={Mail} data={totals.email} />
          <TotalCard channel="sms" label="SMS" icon={MessageSquare} data={totals.sms} />
        </div>

        {/* Notes */}
        <div className="space-y-1 text-xs text-muted-foreground">
          {data?.email_note && <p>• {data.email_note}</p>}
          {data?.sms_note && <p>• {data.sms_note}</p>}
        </div>

        {/* Per-event matrix */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Delivery by operational event</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="delivery-matrix">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2">Event</th>
                  {CHANNELS.map((c) => (
                    <th key={c.key} className="px-3 py-2">
                      <span className="inline-flex items-center gap-1"><c.icon className="w-3.5 h-3.5" />{c.label}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={4} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>
                )}
                {!loading && (data?.buckets || []).map((b) => {
                  const anyFail = CHANNELS.some((c) => (b.channels?.[c.key]?.failed || 0) > 0);
                  return (
                    <tr key={b.key} className={`border-b hover:bg-muted/40 ${anyFail ? 'bg-rose-50/60 dark:bg-rose-950/20' : ''}`} data-testid={`bucket-row-${b.key}`}>
                      <td className="px-3 py-2 font-medium align-top">
                        <span className="inline-flex items-center gap-1">
                          {anyFail && <AlertTriangle className="w-3.5 h-3.5 text-rose-500" />}{b.label}
                        </span>
                      </td>
                      {CHANNELS.map((c) => <Cell key={c.key} ch={b.channels?.[c.key]} channel={c.key} />)}
                    </tr>
                  );
                })}
                {!loading && (data?.buckets || []).length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-8 text-center text-muted-foreground">No delivery activity in this window.</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Recent failures */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="w-4 h-4 text-rose-500" /> Recent failures ({failures.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {failures.length === 0 ? (
              <div className="px-3 py-8 text-center text-emerald-600 flex items-center justify-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> No delivery failures in this window.
              </div>
            ) : (
              <table className="w-full text-xs" data-testid="failures-table">
                <thead>
                  <tr className="border-b text-left uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2">Channel</th>
                    <th className="px-3 py-2">Event</th>
                    <th className="px-3 py-2">Recipient</th>
                    <th className="px-3 py-2">Error</th>
                    <th className="px-3 py-2">Provider ID</th>
                    <th className="px-3 py-2">When</th>
                  </tr>
                </thead>
                <tbody>
                  {failures.map((f, i) => (
                    <tr key={i} className="border-b hover:bg-muted/40" data-testid={`failure-row-${i}`}>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="uppercase">{f.channel}</Badge>
                      </td>
                      <td className="px-3 py-2">{f.event}</td>
                      <td className="px-3 py-2 font-mono">{f.recipient}</td>
                      <td className="px-3 py-2 text-rose-600 max-w-[280px] truncate" title={f.error}>{f.error}</td>
                      <td className="px-3 py-2 font-mono text-muted-foreground">{f.provider_id || '—'}</td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{when(f.at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <p className="text-[11px] text-muted-foreground text-right">
          Generated {when(data?.generated_at)} · in-app / email / SMS reported separately · is_read is never used as a delivery signal.
        </p>
      </div>
    </div>
  );
};

export default DeliveryHealth;
